import type { QueryClient } from '@tanstack/react-query';
import type { ResourceType } from '@medplum/fhirtypes';
import { medplumQueryKeys } from './query-keys';

/**
 * Utility functions for managing Medplum query cache invalidation.
 */
export const medplumCacheUtilities = {
  /**
   * Invalidates all Medplum queries in the cache.
   *
   * @param queryClient - The TanStack Query client instance.
   *
   * @example
   * ```ts
   * await medplumCacheUtilities.invalidateAll(queryClient);
   * ```
   */
  invalidateAll: async (queryClient: QueryClient): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: medplumQueryKeys.all });
  },

  /**
   * Invalidates all queries for a specific resource type.
   *
   * @param queryClient - The TanStack Query client instance.
   * @param resourceType - The FHIR resource type to invalidate.
   *
   * @example
   * ```ts
   * // Invalidate all Patient queries
   * await medplumCacheUtilities.invalidateResource(queryClient, 'Patient');
   * ```
   */
  invalidateResource: async (queryClient: QueryClient, resourceType: ResourceType): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: medplumQueryKeys.resource(resourceType) });
  },

  /**
   * Invalidates all search queries for a specific resource type.
   *
   * @param queryClient - The TanStack Query client instance.
   * @param resourceType - The FHIR resource type to invalidate.
   *
   * @example
   * ```ts
   * // Invalidate all Patient search queries
   * await medplumCacheUtilities.invalidateSearches(queryClient, 'Patient');
   * ```
   */
  invalidateSearches: async (queryClient: QueryClient, resourceType: ResourceType): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: medplumQueryKeys.searches(resourceType) });
  },

  /**
   * Removes all Medplum queries from the cache.
   *
   * @param queryClient - The TanStack Query client instance.
   *
   * @example
   * ```ts
   * medplumCacheUtilities.removeAll(queryClient);
   * ```
   */
  removeAll: (queryClient: QueryClient): void => {
    queryClient.removeQueries({ queryKey: medplumQueryKeys.all });
  },

  /**
   * Removes all queries for a specific resource type from the cache.
   *
   * @param queryClient - The TanStack Query client instance.
   * @param resourceType - The FHIR resource type to remove.
   *
   * @example
   * ```ts
   * // Remove all Patient queries from cache
   * medplumCacheUtilities.removeResource(queryClient, 'Patient');
   * ```
   */
  removeResource: (queryClient: QueryClient, resourceType: ResourceType): void => {
    queryClient.removeQueries({ queryKey: medplumQueryKeys.resource(resourceType) });
  },

  /**
   * Resets all Medplum queries to their initial state.
   *
   * @param queryClient - The TanStack Query client instance.
   *
   * @example
   * ```ts
   * await medplumCacheUtilities.resetAll(queryClient);
   * ```
   */
  resetAll: async (queryClient: QueryClient): Promise<void> => {
    await queryClient.resetQueries({ queryKey: medplumQueryKeys.all });
  },

  /**
   * Resets all queries for a specific resource type to their initial state.
   *
   * @param queryClient - The TanStack Query client instance.
   * @param resourceType - The FHIR resource type to reset.
   *
   * @example
   * ```ts
   * // Reset all Patient queries
   * await medplumCacheUtilities.resetResource(queryClient, 'Patient');
   * ```
   */
  resetResource: async (queryClient: QueryClient, resourceType: ResourceType): Promise<void> => {
    await queryClient.resetQueries({ queryKey: medplumQueryKeys.resource(resourceType) });
  },

  /**
   * Refetches all Medplum queries.
   *
   * @param queryClient - The TanStack Query client instance.
   *
   * @example
   * ```ts
   * await medplumCacheUtilities.refetchAll(queryClient);
   * ```
   */
  refetchAll: async (queryClient: QueryClient): Promise<void> => {
    await queryClient.refetchQueries({ queryKey: medplumQueryKeys.all });
  },

  /**
   * Refetches all queries for a specific resource type.
   *
   * @param queryClient - The TanStack Query client instance.
   * @param resourceType - The FHIR resource type to refetch.
   *
   * @example
   * ```ts
   * // Refetch all Patient queries
   * await medplumCacheUtilities.refetchResource(queryClient, 'Patient');
   * ```
   */
  refetchResource: async (queryClient: QueryClient, resourceType: ResourceType): Promise<void> => {
    await queryClient.refetchQueries({ queryKey: medplumQueryKeys.resource(resourceType) });
  },
} as const;
